import { useEffect, useMemo, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { PaginationResponseDto } from '@tiendanube/common';
import { ProductsWithoutStockOrganizeEnum } from '@tiendanube/common/src/domains/products/enums';
import { Country } from '@tiendanube/common/src/enums';
import { InterfaceNameValue, Tabs } from '@tiendanube/components';
import { CheckCircleIcon } from '@tiendanube/icons';
import { ModalConfirmationBase } from 'App/components';
import ShowByCountry from 'App/components/ShowByCountry';
import { useToastProgress } from 'App/components/ToastProgressContext/ToastProgressContext';
import HelpLink from 'App/HelpLink';
import { useNavegate } from 'App/hooks';
import {
  ActionProp,
  ActionsDesktop,
  CancelAndSaveButtons,
  CardWithTitle,
  ErrorScreen,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
  Stack,
} from 'commons/components';
import { Subtitle } from 'commons/components/IonPageStratus/components/HeaderContent/components';
import { HELP_LINKS_ORGANIZE_PRODUCTS } from 'commons/constants';
import {
  useAsyncFunc,
  useForm,
  useModal,
  useToast,
  useTranslationLanguage,
} from 'commons/hooks';
import { InterfaceNameBooleanValue } from 'commons/types';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import {
  SortBySelectType,
  updateProductsOrder,
  useProductOrder,
} from 'domains/Online';
import useProductOrderList from 'domains/Online/Themes/hooks/useProductOrderList';
import { SectionProductItemInterface } from 'domains/Online/Themes/types';
import { FeaturedProducts, OrganizeProducts } from './components';
import ConfigureProductsSkeleton from './components/ConfigureProductsSkeleton';
import ProductsWithoutStockCard from './components/OrganizeProducts/components/ProductsWithoutStockCard';
import ProductsWithoutStockSearchCard from './components/OrganizeProducts/components/ProductsWithoutStockSearchCard';
import OrganizeProductsSkeleton from './components/OrganizeProductsSkeleton';
import useGetMaxProductSort from './useGetMaxProductSort';
import { trackingProductOrganizeActionMoved } from '../../tracking';

export type OrganizeProductsValuesType = {
  noStockProductToBottom: ProductsWithoutStockOrganizeEnum;
  searchNoStockProductToBottom: ProductsWithoutStockOrganizeEnum;
  categoryId: string;
  sortBy: SortBySelectType;
  order: Record<string, number>;
};

const TAB_FEATURED = 0;
const TAB_ORGANIZE = 1;
const TAB_CONFIGURATION = 2;

// eslint-disable-next-line max-statements
function OrganizeProductsPage() {
  const t = useTranslationCatalog();
  const language = useTranslationLanguage();
  const { goBack } = useNavegate();

  const { addToast } = useToast();
  const { addToastProgress, closeToastProgress } = useToastProgress();

  const [activeTab, setActiveTab] = useState(0);
  const [showModalTabConfirm, openModalTabConfirm, closeModalTabConfirm] =
    useModal();
  const [
    showModalCategoryConfirm,
    openModalCategoryConfirm,
    closeModalCategoryConfirm,
  ] = useModal();
  const [tempCategoryId, setTempCategoryId] = useState<string>();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [savedValue, setSavedValue] = useState<OrganizeProductsValuesType>();
  const totalMovedProducts = useRef(0);

  const { status, options, categories, fetchProductOrder } = useProductOrder();

  const {
    products,
    status: statusList,
    isLoadingRemainingProducts,
    setProducts,
    resetProducts,
    fetchProductOrderlist,
    fetchRemainingProducts,
  } = useProductOrderList();

  const [onSubmit, isSaving] = useAsyncFunc<OrganizeProductsValuesType, void>(
    async (data) => {
      trackingProductOrganizeActionMoved(totalMovedProducts.current);
      totalMovedProducts.current = 0;
      if (!data) return;
      addToastProgress({
        label: t('products.order.toast.progress'),
      });

      await updateProductsOrder(data);
      fetchProductOrder(false);
      setSavedValue(data);
    },
    () => {
      closeToastProgress();
      addToast({
        label: t('products.order.toast.success'),
        appearance: 'success',
      });
      if (tempCategoryId) {
        const categoryId = tempCategoryId as string;
        const sortBy = getSortByFromCategoryId(categoryId);
        getProducts(categoryId, sortBy);
        setFieldValue('categoryId', categoryId);
        setFieldValue('sortBy', sortBy);
        sortBy &&
          setSavedValue({
            ...values,
            categoryId,
            sortBy,
          });
        setTempCategoryId(undefined);
      }
      setIsDirty(false);
    },
    () => {
      closeToastProgress();
      addToast({
        label: t('products.order.toast.error'),
        appearance: 'danger',
      });
    },
  );

  const initialValue = useMemo(
    () =>
      savedValue
        ? savedValue
        : {
            noStockProductToBottom: options?.noStockProductToBottom,
            searchNoStockProductToBottom: options?.searchNoStockProductToBottom,
            categoryId: '0',
            sortBy: options?.allProductsSortBy,
            order: {},
          },
    [
      savedValue,
      options?.allProductsSortBy,
      options?.noStockProductToBottom,
      options?.searchNoStockProductToBottom,
    ],
  ) as OrganizeProductsValuesType;

  const { values, handleOnChange, handleOnSubmit, setFieldValue, resetValues } =
    useForm<OrganizeProductsValuesType>({
      initialValues: initialValue,
      onSubmit,
    });

  const saveActionMobile: ActionProp | undefined =
    activeTab === TAB_ORGANIZE || activeTab === TAB_CONFIGURATION
      ? {
          onClick: handleOnSubmit,
          icon: CheckCircleIcon,
          spinner: isSaving,
          disabled: isSaving,
        }
      : undefined;

  const saveActionsDesktop: ActionProp[] | undefined =
    activeTab === TAB_ORGANIZE || activeTab === TAB_CONFIGURATION
      ? [
          {
            onClick: handleOnSubmit,
            children: t('common:cancelAndSaveButtons.save'),
            appearance: 'primary',
            spinner: isSaving,
            disabled: isSaving,
          },
        ]
      : undefined;

  const handleOnChangeTab = (tab: number) => {
    setActiveTab(tab);
  };

  const getSortByFromCategoryId = (categoryId: string) => {
    const categorySelected = categories?.find(
      (current) => current.id === categoryId,
    );
    return categoryId === '0'
      ? options?.allProductsSortBy
      : categorySelected?.sortBy;
  };

  const handleOnChangeData = (
    data: InterfaceNameValue | InterfaceNameBooleanValue,
  ) => {
    if (statusList === 'loading') return;
    if (data.name === 'categoryId') {
      const categoryId = data.value as string;
      if (isDirty) {
        setTempCategoryId(categoryId);
        openModalCategoryConfirm();
        return;
      }
      const sortBy = getSortByFromCategoryId(categoryId);
      getProducts(categoryId, sortBy);
      setFieldValue('categoryId', categoryId);
      setFieldValue('sortBy', sortBy);
      return;
    }
    if (data.name === 'sortBy') {
      const categoryId = values.categoryId;
      const sortBy = data.value as SortBySelectType;
      getProducts(categoryId, sortBy);
      setFieldValue('sortBy', sortBy);
      setIsDirty(true);
      return;
    }
    setIsDirty(true);
    handleOnChange(data);
  };

  const getProducts = (category?: string, sortBy?: SortBySelectType) => {
    resetProducts();
    fetchProductOrderlist(
      {
        categoryId: category || values.categoryId,
        sortBy: sortBy || values.sortBy,
        page: 1,
        perPage: 50,
      },
      true,
    );
  };

  const maxProductsUserSort = useGetMaxProductSort();

  const isMaxUserSortProducts =
    values.sortBy === 'user' && products.results.length >= maxProductsUserSort;

  const getMoreProducts = () => {
    const page = products.pagination.nextPage;
    const perPage = products.pagination.perPage;
    const hasProducts = products.results.length > 0;

    if (
      statusList === 'loading' ||
      isLoadingRemainingProducts ||
      !hasProducts ||
      isMaxUserSortProducts ||
      page === null
    ) {
      // avoid concurrent fetch
      return;
    }

    fetchProductOrderlist({
      categoryId: values.categoryId,
      sortBy: values.sortBy,
      page: page as number,
      perPage: perPage as number,
    });
  };

  const handleOnChangeProducts = (
    newProductsOrdered: SectionProductItemInterface[],
  ) => {
    setProducts((current) => ({
      pagination: current?.pagination as PaginationResponseDto,
      results: newProductsOrdered,
    }));
    const arr = newProductsOrdered.map((current, idx) => ({
      id: current.productId,
      pos: idx + 1,
    }));
    const obj = arr.reduce(
      (accumulator, value) => ({ ...accumulator, [value.id]: value.pos }),
      {},
    );
    setFieldValue('order', obj);
    setIsDirty(true);
  };

  const handleOnActionModalCategoryConfirm = async () => {
    closeModalCategoryConfirm();
    handleOnSubmit();
  };

  const handleOnCloseModalCategoryConfirm = () => {
    setTempCategoryId(undefined);
    closeModalCategoryConfirm();
  };

  const handleOnActionModalTabConfirm = async () => {
    resetValues();
    getProducts(initialValue.categoryId, initialValue.sortBy);
    setIsDirty(false);
    closeModalTabConfirm();
  };

  const handleOnCloseModalTabConfirm = () => {
    closeModalTabConfirm();
    setActiveTab(TAB_ORGANIZE);
  };

  const handleOnActionMoved = (quantity: number) => {
    totalMovedProducts.current += quantity;
  };

  useEffect(() => {
    fetchProductOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 'success') {
      getProducts(initialValue.categoryId, initialValue.sortBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (activeTab === TAB_FEATURED && status === 'success' && isDirty) {
      openModalTabConfirm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, status, initialValue.categoryId, initialValue.sortBy]);

  const tabItems = [
    <Tabs.Item key="featured" label={t('products.organizeProducts.featured')}>
      <FeaturedProducts />
    </Tabs.Item>,
    <Tabs.Item key="organize" label={t('products.organizeProducts.organize')}>
      <>
        {status === 'loading' && <OrganizeProductsSkeleton />}
        {status === 'error' && (
          <CardWithTitle>
            <ErrorScreen
              onRetry={fetchProductOrder}
              description={t('products.order.error')}
            />
          </CardWithTitle>
        )}
        {status === 'success' && options && (
          <OrganizeProducts
            products={products?.results || []}
            productsStatus={statusList}
            productsCount={products?.pagination.totalResults}
            categoryId={values.categoryId}
            categories={categories || []}
            sortBy={values.sortBy}
            isSaving={isSaving}
            isLoading={statusList === 'loading'}
            isLoadingRemainingProducts={isLoadingRemainingProducts}
            isMaxUserSortProducts={isMaxUserSortProducts}
            onActionMoved={handleOnActionMoved}
            onEnded={getMoreProducts}
            onChange={handleOnChangeData}
            onChangeProducts={handleOnChangeProducts}
            onSubmit={handleOnSubmit}
            onFetchRemainingProducts={fetchRemainingProducts}
          />
        )}
      </>
    </Tabs.Item>,
    <Tabs.Item
      key="configuration"
      label={t('products.organizeProducts.configuration')}
    >
      {status === 'loading' ? (
        <ConfigureProductsSkeleton />
      ) : (
        <Stack column align="stretch">
          <ProductsWithoutStockCard
            noStockProductToBottom={values.noStockProductToBottom}
            onChange={handleOnChangeData}
            customTitle={t('products.order.productsWithoutStockCard.titleList')}
            customSubtitle
            customStockHiddenTitle={t(
              'products.order.productsWithoutStockCard.stockHiddenList',
            )}
          />
          <ProductsWithoutStockSearchCard
            searchNoStockProductToBottom={values.searchNoStockProductToBottom}
            onChange={handleOnChangeData}
          />
          <CancelAndSaveButtons
            isDisabled={isSaving}
            isLoading={isSaving}
            onSave={handleOnSubmit}
            onCancel={goBack}
          />
        </Stack>
      )}
    </Tabs.Item>,
  ];

  return (
    <IonPageStratus
      width="small"
      headerTop={
        <HeaderTop
          navigation={{
            onClick: goBack,
          }}
          mainAction={saveActionMobile}
        />
      }
      headerContent={
        <HeaderContent
          title={t('products.organizeProducts.title')}
          actions={
            saveActionsDesktop && (
              <ActionsDesktop actions={saveActionsDesktop} />
            )
          }
        />
      }
    >
      <Subtitle text={t('products.organizeProducts.description')} />
      <Prompt when={isDirty} message={t('common:exitEdit.info')} />
      <Tabs onChange={handleOnChangeTab} activeTab={activeTab}>
        {tabItems}
      </Tabs>
      {showModalTabConfirm && (
        <ModalConfirmationBase
          show
          onConfirm={handleOnActionModalTabConfirm}
          onCancel={handleOnCloseModalTabConfirm}
        />
      )}
      {showModalCategoryConfirm && (
        <ModalConfirmationBase
          show
          title={t('products.order.modalConfirmation.title')}
          text={t('products.order.modalConfirmation.body')}
          label={t('products.order.modalConfirmation.confirm')}
          labelCancel={t('products.order.modalConfirmation.continue')}
          appearance="primary"
          onConfirm={handleOnActionModalCategoryConfirm}
          onCancel={handleOnCloseModalCategoryConfirm}
        />
      )}

      <ShowByCountry excludeCountries={[Country.BR]}>
        <HelpLink
          title={t('helpLink.organizeProducts')}
          previousValue=""
          currentViewTracking={t('helpLink.organizeProductsTrackingName')}
          linkURL={HELP_LINKS_ORGANIZE_PRODUCTS[language]}
          icon="both"
        />
      </ShowByCountry>
    </IonPageStratus>
  );
}

export default OrganizeProductsPage;
